// hooks/useSiteConfig.ts - Fetches site configuration from PHP backend
import { useState, useEffect } from 'react';

export interface Holiday {
  date: string;
  end_date?: string;
  name_fi: string;
  name?: string;
  type: 'closed' | 'special';
}

export interface SiteConfig {
  prices?: {
    materials?: Record<string, number>;
    size_multipliers?: Record<string, number>;
    blade_factors?: Record<string, number>;
    services?: Record<string, number>;
    urgency?: Record<string, number>;
    duoprop_multiplier?: number;
  };
  business_hours?: Record<string, { open: string; close: string; enabled: boolean }>;
  holidays?: Holiday[];
  updated_at?: string;
}

export function useSiteConfig() {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Add cache-busting parameter to get fresh data
        const response = await fetch('/data/site-config.json?t=' + Date.now());
        if (!response.ok) {
          throw new Error('Failed to fetch config');
        }
        const data = await response.json();
        setConfig(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return { config, loading, error };
}

// Helper to check if a date range includes today
export function isHolidayActive(holiday: Holiday): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = new Date(holiday.date);
  startDate.setHours(0, 0, 0, 0);

  const endDate = holiday.end_date ? new Date(holiday.end_date) : startDate;
  endDate.setHours(23, 59, 59, 999);

  return today >= startDate && today <= endDate;
}

// Get upcoming holidays (within next 30 days)
export function getUpcomingHolidays(holidays: Holiday[] = [], days: number = 30): Holiday[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + days);

  return holidays.filter(holiday => {
    const startDate = new Date(holiday.date);
    startDate.setHours(0, 0, 0, 0);

    // Include if start date is today or in the future, and within range
    return startDate >= today && startDate <= futureDate;
  }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}
